import { useEffect, useState } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import {
  Card,
  ManagementListToolbar,
  RecordFormDialog,
  PaginatedList,
  StatusPill,
  ServerPagination,
  formatRecordDate,
} from "@taunggyi-plus/design-system";

type Order = { id: string; service: string; label: string; status: string; finished: boolean };
export function OrderSupportDialog({
  initialOrder,
  onClose,
  onSaved,
}: {
  initialOrder: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { client } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [selection, setSelection] = useState(initialOrder);
  const [kind, setKind] = useState("support");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const order = orders.find((o) => `${o.service}:${o.id}` === selection);
  useEffect(() => {
    let active = true;
    if (client)
      void client.rpc("my_service_orders").then((r) => {
        if (!active) return;
        setLoading(false);
        if (r.error) setError(r.error.message);
        else setOrders((r.data ?? []) as Order[]);
      });
    return () => {
      active = false;
    };
  }, [client]);
  return (
    <RecordFormDialog title="Order support" open hideTrigger onClose={onClose}>
      <form
        className="form-grid"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!client || !order || busy || (kind === "review" && !order.finished)) return;
          const form = new FormData(e.currentTarget);
          const message = String(form.get("message") ?? "").trim();
          if (message.length < (kind === "review" ? 3 : 5)) {
            setError("Please enter a longer message.");
            return;
          }
          setBusy(true);
          setError("");
          try {
            const result = await client.rpc(
              kind === "review" ? "review_completed_service" : "open_service_case",
              {
                service: order.service,
                order_id: order.id,
                ...(kind === "review"
                  ? { rating: Number(form.get("rating")), comment: message }
                  : { kind, reason: message }),
              },
            );
            if (result.error) throw new Error(result.error.message);
            onSaved();
          } catch (err) {
            setError(err instanceof Error ? err.message : "Could not submit. Please try again.");
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="field">
          Order
          <select
            required
            value={selection}
            disabled={loading || busy}
            onChange={(e) => {
              setSelection(e.target.value);
              setKind("support");
            }}
          >
            <option value="">{loading ? "Loading orders…" : "Choose an order"}</option>
            {orders.map((o) => (
              <option key={`${o.service}:${o.id}`} value={`${o.service}:${o.id}`}>
                {o.label} · {o.service} · #{o.id.slice(0, 8)} · {o.status}
              </option>
            ))}
          </select>
        </label>
        {!loading && !error && !orders.length && <p>No orders available for support yet.</p>}
        <label className="field">
          Request type
          <select value={kind} disabled={busy} onChange={(e) => setKind(e.target.value)}>
            <option value="support">Order support</option>
            <option value="refund">Refund request</option>
            <option value="review" disabled={!order?.finished}>
              Review a completed service
            </option>
          </select>
        </label>
        {!order?.finished && <small>Reviews are available after the service is completed.</small>}
        {kind === "review" && (
          <label className="field">
            Rating
            <select name="rating" disabled={busy}>
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={n} value={n}>
                  {n} / 5
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          Message
          <textarea
            name="message"
            required
            minLength={kind === "review" ? 3 : 5}
            maxLength={1000}
            rows={4}
            disabled={busy}
          />
        </label>
        {error && (
          <p role="alert" className="form-message">
            {error}
          </p>
        )}
        <button className="button" disabled={loading || busy || !order}>
          {busy ? "Submitting…" : "Submit request"}
        </button>
      </form>
    </RecordFormDialog>
  );
}

type Request = {
  id: string;
  kind: string;
  service: string;
  order_id: string;
  status: string;
  message: string;
  resolution: string;
  rating: number | null;
  created_at: string;
  refund_minor: number;
  refund_reference: string;
};
export function CustomerSupportRequests({ onSupport }: { onSupport?: () => void }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const { client } = useAuth();
  const [items, setItems] = useState<Request[]>([]),
    [total, setTotal] = useState(0),
    [page, setPage] = useState(0),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    if (client)
      void client
        .rpc("customer_support_requests", {
          page,
          query,
          kind,
          target: new URLSearchParams(window.location.search).get("record"),
        })
        .then((r) => {
          if (!active) return;
          setLoading(false);
          if (r.error) setError(r.error.message);
          else {
            const data = r.data as { items: Request[]; total: number };
            setItems((old) =>
              page === 0
                ? data.items
                : [...old.filter((o) => !data.items.some((n) => n.id === o.id)), ...data.items],
            );
            setTotal(data.total);
            setError("");
          }
        });
    return () => {
      active = false;
    };
  }, [client, page, retry, query, kind]);
  const selectedId = new URLSearchParams(window.location.search).get("record");
  if (selectedId) {
    const request = items.find((item) => item.id === selectedId);
    const title =
      request?.kind === "review"
        ? "Service review"
        : request?.kind === "refund"
          ? "Refund request"
          : "Order support";
    const service =
      { taxi: "Taxi ride", hotel: "Hotel stay", merchant: "Food order", delivery: "Delivery" }[
        request?.service ?? ""
      ] ?? request?.service;
    return (
      <article className="record-detail-page customer-request-detail">
        <a className="request-back-link" href="/orders?tab=requests">
          <span aria-hidden="true">←</span> Back to requests
        </a>
        {loading ? (
          <div className="request-detail-card" role="status">
            Loading request…
          </div>
        ) : error ? (
          <div className="request-detail-card" role="alert">
            <p>{error}</p>
            <button
              className="button button--secondary"
              onClick={() => {
                setLoading(true);
                setRetry((value) => value + 1);
              }}
            >
              Try again
            </button>
          </div>
        ) : !request ? (
          <div className="request-detail-card">
            <h1>Request unavailable</h1>
            <p>This request could not be found in your account.</p>
          </div>
        ) : (
          <>
            <header className="request-detail-heading">
              <div className="request-detail-title">
                <span className="request-detail-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  >
                    <path d="M5 4h14v13H9l-4 4V4Z" />
                    <path d="M8 8h8M8 12h5" />
                  </svg>
                </span>
                <div>
                  <p className="request-reference">
                    REQUEST #{request.id.slice(0, 8).toUpperCase()}
                  </p>
                  <h1>{title}</h1>
                </div>
              </div>
              <StatusPill
                tone={
                  request.status === "resolved" || request.kind === "review"
                    ? "success"
                    : request.status === "rejected"
                      ? "warning"
                      : "neutral"
                }
              >
                {request.status.replaceAll("_", " ")}
              </StatusPill>
            </header>
            <div className="request-detail-grid">
              <div className="request-detail-conversation">
                <section className="request-detail-card" aria-labelledby="request-message-title">
                  <div className="request-section-heading">
                    <h2 id="request-message-title">
                      {request.kind === "review" ? "Your review" : "Your message"}
                    </h2>
                    <time dateTime={request.created_at}>
                      {formatRecordDate(request.created_at, "en", true)}
                    </time>
                  </div>
                  {request.rating != null && (
                    <p className="request-rating" aria-label={`Rated ${request.rating} out of 5`}>
                      {"★".repeat(request.rating)}
                      <span>{"☆".repeat(5 - request.rating)}</span>
                      <small>{request.rating} / 5</small>
                    </p>
                  )}
                  <p className="request-message-text">{request.message}</p>
                </section>
                {request.kind !== "review" && (
                  <section
                    className={`request-detail-card ${request.resolution ? "" : "request-response-pending"}`}
                    aria-labelledby="request-response-title"
                  >
                    <h2 id="request-response-title">Response</h2>
                    {request.resolution ? (
                      <p className="request-message-text">{request.resolution}</p>
                    ) : (
                      <div className="request-awaiting">
                        <span aria-hidden="true">◷</span>
                        <div>
                          <strong>Waiting for a response</strong>
                          <p>You’ll receive a notification when your request is updated.</p>
                        </div>
                      </div>
                    )}
                    {request.refund_minor > 0 && (
                      <div className="request-refund">
                        <span>Refund recorded</span>
                        <strong>{(request.refund_minor / 100).toLocaleString()} MMK</strong>
                        {request.refund_reference && (
                          <small>Reference: {request.refund_reference}</small>
                        )}
                      </div>
                    )}
                  </section>
                )}
              </div>
              <aside
                className="request-detail-card request-details-summary"
                aria-labelledby="request-details-title"
              >
                <h2 id="request-details-title">Request details</h2>
                <dl>
                  <div>
                    <dt>Service</dt>
                    <dd>{service}</dd>
                  </div>
                  <div>
                    <dt>Order</dt>
                    <dd>#{request.order_id.slice(0, 8).toUpperCase()}</dd>
                  </div>
                  <div>
                    <dt>Submitted</dt>
                    <dd>{formatRecordDate(request.created_at, "en", true)}</dd>
                  </div>
                </dl>
                <a
                  className="request-order-link"
                  href={`/orders?list=Order%20pages&record=${encodeURIComponent(request.order_id)}`}
                >
                  View order <span aria-hidden="true">↗</span>
                </a>
              </aside>
            </div>
          </>
        )}
      </article>
    );
  }
  return (
    <section className="customer-request-list" aria-label="Submitted requests">
      <div className="request-list-heading">
        <h1>Request</h1>
        <ManagementListToolbar
          actions={
            onSupport && (
              <button
                type="button"
                className="toolbar-icon-button"
                aria-label="Order support"
                title="Order support"
                aria-haspopup="dialog"
                onClick={onSupport}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden="true"
                >
                  <path d="M4 13v-1a8 8 0 0 1 16 0v1M4 12H3v6h4v-6H4Zm16 0h1v6h-4v-6h3Zm0 6v1a2 2 0 0 1-2 2h-6" />
                </svg>
              </button>
            )
          }
          query={query}
          searchLabel="Search requests"
          onQueryChange={(value) => {
            setPage(0);
            setLoading(true);
            setQuery(value);
            setRetry((v) => v + 1);
          }}
          resultCount={total}
          filters={[
            {
              label: "Request type",
              value: kind,
              onChange: (value) => {
                setPage(0);
                setLoading(true);
                setKind(value);
                setRetry((v) => v + 1);
              },
              options: [
                { value: "all", label: "All requests" },
                { value: "support", label: "Order support" },
                { value: "refund", label: "Refund request" },
                { value: "review", label: "Service review" },
              ],
            },
          ]}
          onClear={() => {
            setPage(0);
            setQuery("");
            setKind("all");
            setRetry((v) => v + 1);
          }}
        />
      </div>
      {error && (
        <div className="request-list-state" role="alert">
          <h2>Couldn’t load your requests</h2>
          <p>{error}</p>
          <button
            className="button button--secondary"
            onClick={() => {
              setLoading(true);
              setError("");
              setRetry((v) => v + 1);
            }}
          >
            Try again
          </button>
        </div>
      )}
      {loading && (
        <div className="request-list-state" role="status">
          Loading requests…
        </div>
      )}
      {!loading && !error && !items.length && (
        <div className="request-list-state">
          <h2>No requests yet</h2>
          <p>Your support requests, refunds and reviews will appear here.</p>
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <PaginatedList
          pagination="external"
          label="Request pages"
          items={items}
          renderItem={(r) => (
            <Card className="request-list-record" key={r.id}>
              <h3>
                {r.kind === "review"
                  ? "Service review"
                  : r.kind === "refund"
                    ? "Refund request"
                    : "Order support"}
              </h3>
              <StatusPill
                tone={r.status === "resolved" || r.kind === "review" ? "success" : "neutral"}
              >
                {r.status.replaceAll("_", " ")}
              </StatusPill>
              <p>{r.message}</p>
              <small>
                {r.service} · Order #{r.order_id.slice(0, 8)} ·{" "}
                {formatRecordDate(r.created_at, "en", true)}
              </small>
              {r.rating && <p>Rating: {r.rating} / 5</p>}
              {r.resolution && <p>Response: {r.resolution}</p>}
              {r.refund_minor > 0 && (
                <p>
                  Refund recorded: {(r.refund_minor / 100).toLocaleString()} MMK ·{" "}
                  {r.refund_reference}
                </p>
              )}
            </Card>
          )}
        />
      )}
      {!error && total > 10 && !new URLSearchParams(window.location.search).has("record") && (
        <ServerPagination
          page={page}
          total={total}
          loading={loading}
          onPage={(p) => {
            setLoading(true);
            setPage(p);
          }}
        />
      )}
    </section>
  );
}
